"use client";

import React from "react";
import "./Diwali.css";

const range = (n, step, off = 0) =>
  Array.from({ length: n }, (_, i) => i * step + off);
const P8 = range(8, 45);
const P12 = range(12, 30);
const P6 = range(6, 60);
const DOTS16 = range(16, 22.5);
const DOTS24 = range(24, 15);

// Shared rangoli symbols (defined once, instantiated with <use>)
function RangoliSymbols() {
  return (
    <svg
      className="dw-symbols"
      width="0"
      height="0"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id="dwRgCore">
          <stop offset="0" stopColor="#fff3b0" />
          <stop offset="1" stopColor="#ffb300" />
        </radialGradient>

        {/* A: eight-petal flower */}
        <symbol id="dw-rg-a" viewBox="0 0 120 120">
          <g transform="translate(60 60)">
            {DOTS16.map((a) => (
              <circle
                key={a}
                cx="0"
                cy="-57"
                r="2.2"
                fill="#ffb300"
                transform={`rotate(${a})`}
              />
            ))}
            {P8.map((a) => (
              <g key={a} transform={`rotate(${a})`}>
                <path
                  d="M0 -14 C12 -28 12 -44 0 -54 C-12 -44 -12 -28 0 -14Z"
                  fill="#e53935"
                />
                <path
                  d="M0 -20 C6 -28 6 -37 0 -45 C-6 -37 -6 -28 0 -20Z"
                  fill="#ff9800"
                />
                <circle cx="0" cy="-50" r="2.6" fill="#fff" />
              </g>
            ))}
            {P8.map((a) => (
              <g key={`s${a}`} transform={`rotate(${a + 22.5})`}>
                <path
                  d="M0 -26 L6 -40 L0 -54 L-6 -40Z"
                  fill="#8e24aa"
                  opacity=".85"
                />
                <circle cx="0" cy="-34" r="2" fill="#ffd54f" />
              </g>
            ))}
            <circle r="15" fill="url(#dwRgCore)" />
            <circle r="8" fill="#d81b60" />
            <circle r="3.5" fill="#fff8e1" />
          </g>
        </symbol>

        {/* B: dotted mandala */}
        <symbol id="dw-rg-b" viewBox="0 0 120 120">
          <g transform="translate(60 60)">
            {DOTS24.map((a, i) => (
              <circle
                key={a}
                cx="0"
                cy="-56"
                r="2.4"
                fill={i % 2 ? "#ffd54f" : "#ff7043"}
                transform={`rotate(${a})`}
              />
            ))}
            {P12.map((a, i) => (
              <path
                key={a}
                d="M0 -16 C9 -28 9 -40 0 -50 C-9 -40 -9 -28 0 -16Z"
                fill={i % 2 ? "#ec407a" : "#7b1fa2"}
                transform={`rotate(${a})`}
              />
            ))}
            {P12.map((a) => (
              <circle
                key={`d${a}`}
                cx="0"
                cy="-33"
                r="2.3"
                fill="#fff"
                transform={`rotate(${a})`}
              />
            ))}
            <circle r="17" fill="#ffb300" />
            <circle r="12" fill="#e53935" />
            <circle r="6" fill="#ffd54f" />
            <circle r="2.4" fill="#fff" />
          </g>
        </symbol>

        {/* C: six-petal lotus */}
        <symbol id="dw-rg-c" viewBox="0 0 120 120">
          <g transform="translate(60 60)">
            {P6.map((a) => (
              <g key={a} transform={`rotate(${a})`}>
                <path
                  d="M0 -10 C20 -26 20 -46 0 -58 C-20 -46 -20 -26 0 -10Z"
                  fill="#ec407a"
                />
                <path
                  d="M0 -18 C9 -28 9 -40 0 -50 C-9 -40 -9 -28 0 -18Z"
                  fill="#ffd54f"
                />
              </g>
            ))}
            {P6.map((a) => (
              <circle
                key={`c${a}`}
                cx="0"
                cy="-30"
                r="3"
                fill="#ff6f00"
                transform={`rotate(${a + 30})`}
              />
            ))}
            <circle r="11" fill="#ff6f00" />
            <circle r="5" fill="#fff8e1" />
          </g>
        </symbol>

        {/* Diya */}
        <symbol id="dw-diya-sym" viewBox="0 0 60 40">
          <path
            d="M6 22 H54 C52 34 42 38 30 38 C18 38 8 34 6 22Z"
            fill="#c62828"
            stroke="#ffb300"
            strokeWidth="2"
          />
          <path
            d="M14 27 H46"
            stroke="#ffd54f"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </symbol>
      </defs>
    </svg>
  );
}

const Use = ({ id }) => (
  <svg viewBox="0 0 120 120" className="dw-rg-svg" aria-hidden="true">
    <use href={`#${id}`} width="120" height="120" />
  </svg>
);

// Medium/small rangoli scattered across the white header (pos in % of width)
const SCATTER = [
  // { id: "dw-rg-c", left: "9%", size: 30, top: 6, cls: "dw-s-xl", o: 0.55 },
  // { id: "dw-rg-b", left: "17%", size: 40, top: -10, cls: "dw-s-lg", o: 0.4 },
  // { id: "dw-rg-c", left: "27%", size: 26, top: 52, cls: "dw-s-lg", o: 0.5 },
  // { id: "dw-rg-a", left: "36%", size: 44, top: 28, cls: "dw-s-xl", o: 0.32 },
  // { id: "dw-rg-c", left: "47%", size: 26, top: -4, cls: "dw-s-xl", o: 0.5 },
  // { id: "dw-rg-b", left: "56%", size: 40, top: 40, cls: "dw-s-xl", o: 0.34 },
  // { id: "dw-rg-a", left: "66%", size: 30, top: -8, cls: "dw-s-lg", o: 0.45 },
  // { id: "dw-rg-c", left: "74%", size: 28, top: 46, cls: "dw-s-lg", o: 0.5 },
  // { id: "dw-rg-b", left: "83%", size: 34, top: -4, cls: "dw-s-xl", o: 0.4 },
  // { id: "dw-rg-a", left: "91%", size: 28, top: 50, cls: "dw-s-lg", o: 0.5 },
];

const SPARKS = [
  { l: "6%", t: "22%", d: "0s" },
  { l: "21%", t: "66%", d: "0.7s" },
  { l: "33%", t: "14%", d: "1.3s" },
  { l: "44%", t: "70%", d: "0.4s" },
  { l: "59%", t: "18%", d: "1.8s" },
  { l: "70%", t: "72%", d: "1s" },
  { l: "80%", t: "20%", d: "0.2s" },
  { l: "93%", t: "30%", d: "1.5s" },
];

const DIYAS = [
  // { left: "24%", cls: "" },
  { right: "10px", left: "unset", cls: "dw-s-lg" },
  { left: "10px", right: "unset", cls: "dw-s-lg" },
];

const BEADS = ["#ff9800", "#e53935", "#ffc107", "#ff9800", "#ec407a"];

function Toran({ flip }) {
  return (
    <svg
      viewBox="0 0 120 38"
      className={`dw-toran ${flip ? "dw-toran-r" : "dw-toran-l"}`}
      aria-hidden="true"
    >
      <path
        d="M2 2 Q60 12 118 2"
        fill="none"
        stroke="#b8860b"
        strokeWidth="1.4"
      />
      {[14, 34, 54, 74, 94, 110].map((x, i) => {
        const y = 2 + 10 * Math.sin((x / 120) * Math.PI) * 0.9 + 1;
        const len = 8 + ((i * 7) % 3) * 6;
        return (
          <g
            key={x}
            className="dw-toran-strand"
            style={{
              animationDelay: `${i * 0.3}s`,
              transformOrigin: `${x}px ${y}px`,
            }}
          >
            <path
              d={`M${x} ${y} V${y + len}`}
              stroke="#b8860b"
              strokeWidth="1"
            />
            {[0, 1, 2].slice(0, len > 14 ? 3 : 2).map((k) => (
              <circle
                key={k}
                cx={x}
                cy={y + 5 + k * 6}
                r="3"
                fill={BEADS[(i + k) % BEADS.length]}
              />
            ))}
            <path
              d={`M${x} ${y + len + 1} l-3 6 h6z`}
              fill={BEADS[(i + 2) % BEADS.length]}
            />
          </g>
        );
      })}
    </svg>
  );
}

export default function DiwaliHeaderDecor() {
  return (
    <div className="dw-header-layer" aria-hidden="true">
      <RangoliSymbols />

      <div className="dw-lights" />
      <div className="dw-gold-border" />
      {/* <div className="dw-border-pattern" /> */}

      {/* corner rangolis */}
      {/* <div className="dw-corner dw-corner-l">
        <Use id="dw-rg-a" />
      </div> */}
      {/* <div className="dw-corner dw-corner-r">
        <Use id="dw-rg-b" />
      </div> */}

      {/* scattered rangolis */}
      {SCATTER.map((r, i) => (
        <span
          key={i}
          className={`dw-scatter ${r.cls}`}
          style={{
            left: r.left,
            top: r.top,
            width: r.size,
            height: r.size,
            opacity: r.o,
          }}
        >
          <Use id={r.id} />
        </span>
      ))}

      {/* sparkles */}
      {SPARKS.map((s, i) => (
        <i
          key={i}
          className={`dw-spark ${i % 2 ? "dw-s-lg" : ""}`}
          style={{ left: s.l, top: s.t, animationDelay: s.d }}
        />
      ))}

      {/* small diyas */}
      {DIYAS.map((d, i) => (
        <span
          key={i}
          className={`dw-hdiya ${d.cls}`}
          style={{ left: d.left, right: d.right }}
        >
          <i className="dw-hdiya-glow" />
          <svg viewBox="0 0 60 40" className="dw-hdiya-svg">
            <path
              className="dw-flame-s"
              d="M30 2 C24 10 25 17 30 21 C35 17 36 10 30 2Z"
              fill="#ffca28"
            />
            <use href="#dw-diya-sym" width="60" height="40" />
          </svg>
        </span>
      ))}

      <Toran />
      {/* <Toran flip /> */}
    </div>
  );
}

// Akash Kandil — rendered inside the logo's <Navbar.Brand> so it hangs directly below the logo.
const GLOW_DOTS = [
  { x: -14, y: 30, d: "0s" },
  { x: 16, y: 44, d: "0.8s" },
  { x: -8, y: 78, d: "1.6s" },
  { x: 12, y: 96, d: "0.4s" },
  { x: -18, y: 62, d: "1.2s" },
  { x: 20, y: 70, d: "2s" },
];

export function DiwaliLantern() {
  return (
    <span className="dw-lantern-wrap" aria-hidden="true">
      <span className="dw-lantern">
        <i className="dw-hook" />
        <i className="dw-thread" />
        <span className="dw-lantern-glow" />
        <svg viewBox="0 0 80 150" className="dw-lantern-svg">
          <defs>
            <linearGradient id="dwLbody" x1="0" x2="1">
              <stop offset="0" stopColor="#b71c1c" />
              <stop offset=".5" stopColor="#ff5722" />
              <stop offset="1" stopColor="#b71c1c" />
            </linearGradient>
            <radialGradient id="dwLglow" cx=".5" cy=".5" r=".5">
              <stop offset="0" stopColor="#fffde7" />
              <stop offset=".5" stopColor="#ffca28" />
              <stop offset="1" stopColor="#ff6f00" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle
            cx="40"
            cy="8"
            r="5"
            fill="none"
            stroke="#ffc107"
            strokeWidth="2"
          />
          <path
            d="M40 13 L22 32 H58Z"
            fill="#ffb300"
            stroke="#8d5a00"
            strokeWidth="1"
          />
          <rect x="19" y="32" width="42" height="5" rx="2" fill="#ffc107" />
          <path
            d="M22 37 C6 60 6 84 22 106 H58 C74 84 74 60 58 37Z"
            fill="url(#dwLbody)"
          />
          <ellipse
            className="dw-flame-glow"
            cx="40"
            cy="72"
            rx="24"
            ry="32"
            fill="url(#dwLglow)"
          />
          <path
            className="dw-flame"
            d="M40 58 C33 68 34 78 40 84 C46 78 47 68 40 58Z"
            fill="#fff59d"
          />
          <path
            d="M32 38 C22 60 22 84 32 106 M48 38 C58 60 58 84 48 106 M40 37 V106"
            fill="none"
            stroke="#ffc107"
            strokeWidth="1.4"
            opacity=".8"
          />
          <rect x="19" y="106" width="42" height="5" rx="2" fill="#ffc107" />
          <path
            d="M24 111 H56 L48 122 H32Z"
            fill="#ffb300"
            stroke="#8d5a00"
            strokeWidth="1"
          />
          <g
            className="dw-tassel"
            stroke="#ffc107"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M34 122 V140 M40 122 V146 M46 122 V140" />
            <circle cx="34" cy="141" r="2.5" fill="#e53935" />
            <circle cx="40" cy="147" r="2.5" fill="#e53935" />
            <circle cx="46" cy="141" r="2.5" fill="#e53935" />
          </g>
        </svg>
        {GLOW_DOTS.map((g, i) => (
          <i
            key={i}
            className="dw-gdot"
            style={{
              left: `calc(50% + ${g.x}px)`,
              top: g.y,
              animationDelay: g.d,
            }}
          />
        ))}
      </span>
    </span>
  );
}

export function DiwaliLanterns() {
  return (
    <>
      <div className="dw-lantern-slot dw-lantern-slot-l" aria-hidden="true">
        <DiwaliLantern idSuffix="L" />
      </div>
      <div className="dw-lantern-slot dw-lantern-slot-r" aria-hidden="true">
        <DiwaliLantern idSuffix="R" />
      </div>
    </>
  );
}
